<template>
  <div class="template-textarea-container">
    <label v-if="label" class="template-textarea-label">{{ label }}<vue-feather v-if="tooltip" type="info" size="14" class="label-tooltip-icon" v-tooltip="tooltip" /></label>

    <div class="template-textarea-wrapper">
      <!-- Background highlights for variables -->
      <div v-if="!disabled" class="template-textarea-highlights" :style="{ height: textareaHeight }">
        <span v-for="(part, idx) of textParts" :key="idx">
          <span v-if="isVariable(part)" :class="getVariableHighlightClass(part)">{{ part }}</span>
          <span v-else>{{ part }}</span>
        </span>
      </div>

      <!-- Textarea input (hidden when disabled) -->
      <textarea
        v-if="!disabled"
        ref="textarea"
        v-model="internalValue"
        :placeholder="placeholder"
        :rows="currentRows"
        :class="textareaClass"
        class="template-textarea-input"
        @input="handleInput"
        @keydown="handleKeyDown"
        @click="checkAutocomplete"
        @scroll="handleScroll"
        @blur="$emit('blur')"
      />

      <!-- Autocomplete dropdown (positioned near cursor) -->
      <div
        v-if="autocomplete.show && filteredVariables.length > 0"
        class="autocomplete-dropdown"
        :style="{ top: autocomplete.top + 'px', left: autocomplete.left + 'px' }"
      >
        <div
          v-for="(variable, index) in filteredVariables"
          :key="variable"
          class="autocomplete-item"
          :class="{ 'autocomplete-item--selected': index === autocomplete.selectedIndex }"
          @mousedown.prevent="selectVariable(variable)"
          @mouseenter="autocomplete.selectedIndex = index"
        >
          {{ variable }}
        </div>
      </div>
    </div>

    <!-- Preview with highlighted variables -->
    <div v-if="showPreview && internalValue" class="template-preview" :class="{ 'template-preview--disabled': disabled }">
      <div v-if="!disabled" class="template-preview-label">Preview:</div>
      <div class="template-preview-content">
        <span v-for="(part, idx) of textParts" :key="idx">
          <span v-if="isVariable(part)" :class="getVariableClass(part)" class="template-variable-preview">
            {{ part.substring(1, part.length - 1) }}
          </span>
          <span v-else>{{ part }}</span>
        </span>
      </div>
    </div>

    <span v-if="error" class="template-textarea-error">{{ error }}</span>
  </div>
</template>

<script>
import getCaretCoordinates from 'textarea-caret'
import VueFeather from 'vue-feather'

const VARIABLE_REGEX = /{[^}]+}/g

export default {
  name: 'TemplateTextArea',
  components: {
    'vue-feather': VueFeather
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    tooltip: {
      type: String,
      default: ''
    },
    // Min/max rows for auto-resizing
    minRows: {
      type: Number,
      default: 3
    },
    maxRows: {
      type: Number,
      default: 0 // 0 = no max
    },
    // Show preview with highlighted variables
    showPreview: {
      type: Boolean,
      default: true
    },
    // Allowed variable names - array of strings or '**' for all
    allowedVariables: {
      type: [Array, String],
      default: '**'
    },
    // Suggested variable names for autocomplete - array of strings
    suggestions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      internalValue: this.modelValue,
      currentRows: this.minRows,
      textareaHeight: 'auto',
      autocomplete: {
        show: false,
        top: 0,
        left: 0,
        query: '',
        selectedIndex: 0,
        startPos: 0
      }
    }
  },
  computed: {
    textareaClass() {
      return {
        'template-textarea-input--disabled': this.disabled,
        'template-textarea-input--error': this.error
      }
    },
    textParts() {
      return this.getTextParts(this.internalValue)
    },
    filteredVariables() {
      // If no suggestions provided, return empty
      if (!Array.isArray(this.suggestions) || this.suggestions.length === 0) {
        return []
      }

      // Filter based on query
      const query = this.autocomplete.query.toLowerCase()
      if (!query) {
        return this.suggestions
      }

      return this.suggestions.filter(v =>
        v.toLowerCase().includes(query)
      )
    }
  },
  watch: {
    modelValue(newValue) {
      this.internalValue = newValue
      this.$nextTick(() => {
        this.adjustHeight()
      })
    },
    disabled(newVal, oldVal) {
      // When transitioning from disabled to enabled, resize after textarea renders
      if (oldVal && !newVal) {
        this.$nextTick(() => {
          this.adjustHeight()
        })
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.adjustHeight()
    })
  },
  beforeUnmount() {
    this.closeAutocomplete()
  },
  methods: {
    handleInput() {
      this.$emit('update:modelValue', this.internalValue)
      this.$emit('change', this.internalValue)
      this.adjustHeight()
      this.validateVariables()
      this.checkAutocomplete()
    },
    handleScroll(event) {
      // Sync scroll position with highlights
      const highlights = this.$el.querySelector('.template-textarea-highlights')
      if (highlights) {
        highlights.scrollTop = event.target.scrollTop
        highlights.scrollLeft = event.target.scrollLeft
      }
    },
    handleKeyDown(event) {
      if (!this.autocomplete.show) return

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        this.autocomplete.selectedIndex = Math.min(
          this.autocomplete.selectedIndex + 1,
          this.filteredVariables.length - 1
        )
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        this.autocomplete.selectedIndex = Math.max(
          this.autocomplete.selectedIndex - 1,
          0
        )
      } else if (event.key === 'Enter') {
        event.preventDefault()
        if (this.filteredVariables.length > 0) {
          this.selectVariable(this.filteredVariables[this.autocomplete.selectedIndex])
        }
      } else if (event.key === 'Escape') {
        event.preventDefault()
        this.closeAutocomplete()
      }
    },
    checkAutocomplete() {
      const textarea = this.$refs.textarea
      if (!textarea) return

      const cursorPos = textarea.selectionStart
      const text = this.internalValue

      // Find the last '{' before cursor
      let lastOpenBrace = -1
      for (let i = cursorPos - 1; i >= 0; i--) {
        if (text[i] === '{') {
          lastOpenBrace = i
          break
        } else if (text[i] === '}') {
          // Found closing brace first, not in a variable
          break
        }
      }

      // Check if we're inside a variable context
      if (lastOpenBrace !== -1) {
        const query = text.substring(lastOpenBrace + 1, cursorPos)

        // Only show if no closing brace in the query
        if (!query.includes('}')) {
          this.autocomplete.query = query
          this.autocomplete.startPos = lastOpenBrace
          this.autocomplete.selectedIndex = 0
          this.autocomplete.show = true
          this.positionAutocomplete()
          return
        }
      }

      this.closeAutocomplete()
    },
    positionAutocomplete() {
      const textarea = this.$refs.textarea
      if (!textarea) return

      // Use textarea-caret library to get cursor position
      const coords = getCaretCoordinates(textarea, this.autocomplete.startPos)

      // Position dropdown below the cursor
      this.autocomplete.top = coords.top + coords.height + 2
      this.autocomplete.left = coords.left
    },
    selectVariable(variable) {
      const textarea = this.$refs.textarea
      if (!textarea) return

      const cursorPos = textarea.selectionStart
      const text = this.internalValue
      const startPos = this.autocomplete.startPos

      // Replace from '{' to cursor with the full variable
      const before = text.substring(0, startPos)
      const after = text.substring(cursorPos)
      this.internalValue = before + '{' + variable + '}' + after

      // Emit update
      this.$emit('update:modelValue', this.internalValue)
      this.$emit('change', this.internalValue)

      // Close autocomplete
      this.closeAutocomplete()

      // Move cursor after the inserted variable
      this.$nextTick(() => {
        const newPos = startPos + variable.length + 2 // +2 for {}
        textarea.setSelectionRange(newPos, newPos)
        textarea.focus()
      })
    },
    closeAutocomplete() {
      this.autocomplete.show = false
      this.autocomplete.query = ''
      this.autocomplete.selectedIndex = 0
    },
    adjustHeight() {
      const textarea = this.$refs.textarea
      if (!textarea) return

      // If no content, use minRows
      if (!this.internalValue || this.internalValue.trim() === '') {
        this.currentRows = this.minRows
        this.$nextTick(() => {
          if (textarea) {
            this.textareaHeight = textarea.scrollHeight + 'px'
          }
        })
        return
      }

      // Get computed styles
      const styles = window.getComputedStyle(textarea)
      const lineHeight = parseFloat(styles.lineHeight)
      const paddingTop = parseFloat(styles.paddingTop)
      const paddingBottom = parseFloat(styles.paddingBottom)

      // Temporarily shrink rows to 1 and height to auto so scrollHeight
      // reflects actual content size (otherwise it can never shrink)
      const previousRows = textarea.rows
      textarea.rows = 1
      textarea.style.height = 'auto'

      // Calculate content height without padding
      const contentHeight = textarea.scrollHeight - paddingTop - paddingBottom
      const contentLines = Math.max(1, Math.round(contentHeight / lineHeight))

      // Restore immediately to prevent flashing
      textarea.rows = previousRows
      textarea.style.height = ''

      // Apply min/max constraints
      let targetRows = Math.max(contentLines, this.minRows)
      if (this.maxRows > 0) {
        targetRows = Math.min(targetRows, this.maxRows)
      }

      this.currentRows = targetRows

      // Update highlight height to match textarea
      this.$nextTick(() => {
        if (textarea) {
          this.textareaHeight = textarea.scrollHeight + 'px'
        }
      })
    },
    getTextParts(text) {
      if (!text) return []

      const parts = []
      let remaining = text
      let indexVar = remaining.search(VARIABLE_REGEX)

      while (indexVar >= 0) {
        // Push text before variable
        if (indexVar > 0) {
          parts.push(remaining.substring(0, indexVar))
        }

        remaining = remaining.substring(indexVar)

        // Find end of variable
        const endOfVar = remaining.search(/}/) + 1
        if (endOfVar > 0) {
          parts.push(remaining.substring(0, endOfVar))
          remaining = remaining.substring(endOfVar)
        } else {
          // Malformed variable, push rest as text
          parts.push(remaining)
          break
        }

        indexVar = remaining.search(VARIABLE_REGEX)
      }

      // Push remaining text
      if (remaining.length > 0) {
        parts.push(remaining)
      }

      return parts
    },
    isVariable(part) {
      return /^{.+}$/.test(part)
    },
    getVariableClass(part) {
      if (!this.isVariable(part)) return ''

      const varName = part.substring(1, part.length - 1)
      const isAllowed = this.isVariableAllowed(varName)

      return {
        'template-variable': true,
        'template-variable--valid': isAllowed,
        'template-variable--invalid': !isAllowed
      }
    },
    getVariableHighlightClass(part) {
      if (!this.isVariable(part)) return ''

      const varName = part.substring(1, part.length - 1)
      const isAllowed = this.isVariableAllowed(varName)

      return {
        'template-highlight': true,
        'template-highlight--valid': isAllowed,
        'template-highlight--invalid': !isAllowed
      }
    },
    isVariableAllowed(varName) {
      // If '**' then all variables are allowed
      if (this.allowedVariables === '**') return true

      // Check if variable is in allowed list
      if (Array.isArray(this.allowedVariables)) {
        return this.allowedVariables.includes(varName)
      }

      return false
    },
    validateVariables() {
      if (this.allowedVariables === '**') return

      const variables = this.extractVariables(this.internalValue)
      const invalid = variables.filter(v => !this.isVariableAllowed(v))

      if (invalid.length > 0) {
        this.$emit('validation-error', `Invalid variables: ${invalid.join(', ')}`)
      } else {
        this.$emit('validation-error', null)
      }
    },
    extractVariables(text) {
      const matches = text.match(VARIABLE_REGEX) || []
      return matches.map(match => match.substring(1, match.length - 1))
    },
    focus() {
      this.$refs.textarea?.focus()
    }
  },
  emits: ['update:modelValue', 'change', 'validation-error', 'blur']
}
</script>

<style scoped>
.template-textarea-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.template-textarea-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 4px;
}

.label-tooltip-icon {
  color: #9ca3af;
  cursor: help;
  flex-shrink: 0;
}

.template-textarea-wrapper {
  position: relative;
}

.template-textarea-highlights {
  position: absolute;
  top: 1px;
  left: 1px;
  right: 1px;
  width: calc(100% - 2px);
  padding: 12px 14px;
  border: 1px solid transparent;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 1rem;
  font-family: inherit;
  color: transparent;
  background: #FFF;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow: hidden;
  overflow-wrap: break-word;
  pointer-events: none;
  z-index: 1;
}

.template-textarea-input {
  position: relative;
  width: 100%;
  min-height: 45px;
  padding: 12px 14px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 1rem;
  font-family: inherit;
  color: #374151;
  background: transparent;
  transition: border-color 0.15s ease;
  resize: none;
  overflow: hidden;
  line-height: 1.5;
  z-index: 2;
}

.template-textarea-input:hover:not(:disabled) {
  border-color: #9ca3af;
}

.template-textarea-input:focus {
  outline: none;
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.template-textarea-input::placeholder {
  color: #9ca3af;
}

.template-textarea-input--disabled {
  background: #f3f4f6;
  cursor: not-allowed;
  opacity: 0.6;
}

.template-textarea-input--error {
  border-color: #dc2626;
}

.template-textarea-input--error:focus {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

.template-preview {
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 0.875rem;
  line-height: 1.6;
}

.template-preview--disabled {
  padding: 0;
  background: transparent;
  border: none;
}

.template-preview-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.template-preview-content {
  color: #374151;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.template-variable {
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
  transition: all 0.15s ease;
}

.template-variable--valid {
  background: #bfdbfe;
  color: #1e40af;
  border: 1px solid #60a5fa;
  box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.1);
}

.template-variable--invalid {
  background: #fecaca;
  color: #991b1b;
  border: 1px solid #f87171;
  box-shadow: 0 0 0 1px rgba(239, 68, 68, 0.1);
}

/* Simple inline style for disabled preview */
.template-preview--disabled .template-variable-preview {
  padding: 2px 4px;
  border-radius: 2px;
  background: rgba(191, 219, 254, 0.7);
  font-weight: normal;
  border: none;
  color: inherit;
}

/* Textarea Highlights (inline background) */
.template-highlight {
  border-radius: 2px;
}

.template-highlight--valid {
  background: rgba(191, 219, 254, 0.9);
  box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.2);
}

.template-highlight--invalid {
  background: rgba(254, 226, 226, 0.8);
}

.template-textarea-error {
  font-size: 0.8125rem;
  color: #dc2626;
}

/* Autocomplete Dropdown - matches DropdownButton styling */
.autocomplete-dropdown {
  position: absolute;
  background: #FFF;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 180px;
  max-height: 300px;
  overflow-y: auto;
  padding: 4px;
}

.autocomplete-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 4px;
  white-space: nowrap;
  cursor: pointer;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #374151;
  transition: background 0.1s ease;
}

.autocomplete-item:hover {
  background: #f3f4f6;
}

.autocomplete-item--selected {
  background: #f3f4f6;
}
</style>
